import { Button, Card, Chip } from "@design";
import { useMutation } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import { useState } from "react";
import { getAvailabilityMutation } from "@/api";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { useProposalContext } from "../research/ProposalContext";
import { busyItems, slotItems } from "./calendarItems";
import { daysIn, hourRange, type MeetingPicker, peopleOf, type Slot, slotOf } from "./calendarModel";
import { TimeGrid } from "./TimeGrid";
import { useCalendarText } from "./useCalendarText";

/**
 * Times that suit everyone (find_meeting_times, MHS-969): everyone's week with the suggested times on it, and the
 * attendees, who can be left out (the times update without asking the model). Picking a time asks for the meeting in
 * the chat; the assistant then proposes it, and it's approved on its own card like any other write.
 */
export function MeetingPickerCard({ widget }: Readonly<{ widget: MeetingPicker }>) {
  const text = useCalendarText();
  const { t } = text;
  const { readOnly, busy, onDecide } = useProposalContext();
  const [invited, setInvited] = useState<string[]>(widget.attendees);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [sent, setSent] = useState(false);
  const refresh = useMutation(getAvailabilityMutation());

  const people = refresh.data ? peopleOf(refresh.data.people) : widget.people;
  const slots = refresh.data ? refresh.data.slots.flatMap((s) => slotOf(s) ?? []) : widget.slots;
  const organizer = people.find((p) => p.email === widget.organizer) ?? people[0];
  const shown = people.filter((p) => p.email === widget.organizer || invited.includes(p.email));
  const unreadable = shown.filter((p) => p.error).map((p) => p.email);
  const hours = organizer?.hours ?? {
    days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    start: "09:00",
    end: "17:00",
  };
  const days = daysIn(
    widget.window,
    hours,
    slots.map((s) => s.start.slice(0, 10)),
  );
  const items = [
    // Just the person's own calendar (focus time): their meetings' titles help; with others, they'd crowd it.
    ...busyItems(shown, days, (p, b) => text.block(p, b, widget.organizer), { labels: shown.length === 1 }),
    ...slotItems(slots, picked?.start ?? null, (s) => text.slot(s, widget.organizer), setPicked),
  ];
  const locked = readOnly || busy || sent || !onDecide;

  const toggle = (email: string) => {
    const next = invited.includes(email) ? invited.filter((e) => e !== email) : [...invited, email];
    setInvited(next);
    setPicked(null);
    refresh.mutate({
      body: {
        attendees: next,
        start: widget.window.start,
        end: widget.window.end,
        duration_minutes: widget.durationMinutes,
        max_suggestions: Math.max(widget.slots.length, 3),
      },
    });
  };

  const schedule = () => {
    if (!picked || !onDecide) return;
    const message = pickMessage(
      picked,
      invited,
      { subject: widget.subject ?? t("pick.untitled"), timeZone: widget.timeZone },
      text.when(picked.start, picked.end, true),
    );
    if (!message) return;
    setSent(true);
    const content =
      message.key === "pick.message" ? t("pick.message", message.values) : t("pick.messageSolo", message.values);
    onDecide(content, undefined, () => setSent(false));
  };

  return (
    <Card>
      <section aria-label={t("pick.label")} className="space-y-3">
        <div className="space-y-1">
          <Text variant="caption" tone="tertiary" className="inline-flex items-center gap-1.5">
            <CalendarClock size={14} aria-hidden="true" />
            {t("pick.label")}
          </Text>
          <Text variant="h3">{widget.subject ?? t("pick.untitled")}</Text>
          <Text variant="body-sm" tone="secondary">
            {text.length(widget.durationMinutes)}
          </Text>
        </div>
        {widget.attendees.length > 0 && (
          <div className="space-y-1">
            <Text variant="caption" tone="tertiary">
              {t("pick.who")}
            </Text>
            <div className="flex flex-wrap gap-1.5">
              {widget.attendees.map((email) => (
                <Chip
                  key={email}
                  size="sm"
                  selected={invited.includes(email)}
                  disabled={locked}
                  onClick={() => toggle(email)}
                >
                  {widget.names[email] ?? email}
                </Chip>
              ))}
            </div>
          </div>
        )}
        <TimeGrid
          days={days}
          range={hourRange(hours, slots)}
          items={locked ? items.map((i) => ({ ...i, onSelect: undefined })) : items}
          label={t("pick.gridLabel")}
        />
        {unreadable.length > 0 && (
          <Text variant="caption" tone="warning">
            {t("unreadable", {
              people: formatList(unreadable.map((e) => widget.names[e] ?? e)),
              count: unreadable.length,
            })}
          </Text>
        )}
        <SlotChoices slots={slots} picked={picked} locked={locked} onPick={setPicked} loading={refresh.isPending} />
        {refresh.isError && (
          <Text variant="caption" tone="error">
            {t("pick.refreshFailed")}
          </Text>
        )}
        <Button variant="primary" size="sm" disabled={locked || !picked} onClick={schedule}>
          {sent ? t("pick.asked") : t("pick.schedule")}
        </Button>
      </section>
    </Card>
  );
}

// A type (not an interface) so it passes as the translation's interpolation values
type PickValues = { subject: string; when: string; people: string; start: string; end: string; timeZone: string };

const NOT_IN_ADDRESS = /[\s"<>]/;

/** A plain email address: one "@", a dot in the domain, no spaces, quotes or angle brackets (no regex backtracking). */
function isEmail(value: string): boolean {
  const parts = value.split("@");
  if (parts.length !== 2 || NOT_IN_ADDRESS.test(value)) return false;
  const [local, domain] = parts;
  const dot = domain.lastIndexOf(".");
  return local.length > 0 && dot > 0 && dot < domain.length - 1;
}
const LOCAL_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;

/**
 * The message a picked time sends, as the person's own words: so it carries only what this card can vouch for. The
 * title (which can come from mail or documents) is one short quoted line, people are email addresses, and times and
 * the time zone are checked, so nothing in the card's data can slip instructions into what the person "said".
 */
export function pickMessage(
  slot: Slot,
  invited: string[],
  widget: Pick<MeetingPicker, "subject" | "timeZone">,
  when: string,
): { key: "pick.message" | "pick.messageSolo"; values: PickValues } | null {
  if (!LOCAL_TIME.test(slot.start) || !LOCAL_TIME.test(slot.end)) return null;
  const subject = (widget.subject ?? "")
    .replace(/[\r\n\t"“”]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  const people = invited.filter(isEmail);
  const timeZone = Intl.supportedValuesOf("timeZone").includes(widget.timeZone) ? widget.timeZone : "UTC";
  const values = {
    subject,
    when,
    people: formatList(people),
    start: slot.start,
    end: slot.end,
    timeZone,
  };
  return { key: people.length ? "pick.message" : "pick.messageSolo", values };
}

function SlotChoices({
  slots,
  picked,
  locked,
  loading,
  onPick,
}: Readonly<{ slots: Slot[]; picked: Slot | null; locked: boolean; loading: boolean; onPick: (slot: Slot) => void }>) {
  const text = useCalendarText();
  if (loading)
    return (
      <Text variant="caption" tone="tertiary">
        {text.t("pick.updating")}
      </Text>
    );
  if (slots.length === 0)
    return (
      <Text variant="body-sm" tone="secondary">
        {text.t("pick.none")}
      </Text>
    );
  return (
    <div className="flex flex-wrap gap-1.5">
      {slots.map((slot) => (
        <Chip
          key={slot.start}
          size="sm"
          selected={picked?.start === slot.start}
          disabled={locked}
          aria-label={text.slot(slot)}
          onClick={() => onPick(slot)}
        >
          {text.when(slot.start, slot.end)}
        </Chip>
      ))}
      {picked && (
        <Text variant="caption" tone={picked.everyoneFree ? "tertiary" : "warning"} className="basis-full">
          {text.slot(picked)}
        </Text>
      )}
    </div>
  );
}
